import {
  Flex,
  Text,
  SearchInput,
  Stack,
  MenuItem,
  MenuDivider,
  MenuGroup,
  useColorMode,
  useColorStyle,
  Icon,
  Alert,
  AlertCloseButton,
  LinkButton,
} from '@tonic-one/react';
import { ColumnsIcon, ExportIcon } from '@tonic-one/react-icons';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { DetailsTable } from './table';
import ManageColumnsModal from './ManageColumnsModal';
import ExportModal from './ExportModal';
import IconButton from '@Components/IconButton';
import {
  FeedbackFilterStatus,
  FeedbackFilterCategory,
  FeedbackResultCategory,
  ServiceTypeFilter,
  FilterKeyword,
  ActionModalState,
  EuqTimeRangeEnum,
} from '../../type';
import debounce from 'lodash/debounce';
import { useTranslation, Trans } from 'react-i18next';
import { useOrgStore } from '@Models/useOrgModel';
import AddFilterMenu from './AddFilterMenu';
import { DownloadBtn, ApproveBtn, RejectBtn } from '../actionBtn';
import ActionModal from '@Components/Common/ActionModal';
import { FILTER_DEBOUNCE_DELAY } from '../constants';
import {
  useEuqFeedbackQuery,
  useFilters,
  useEuqFeedbackMutations,
  useFilterCounts,
  useMitigationStatusCounts,
} from '../hooks';
import type { RequestSourceFilter } from '../hooks/useFilters';
import customConfig from 'src/config';

// Custom FilterMenuItem component
const FilterMenuItem = ({
  onClick,
  label,
  count,
  isSelected = false,
}: {
  onClick: () => void;
  label: string;
  count?: number;
  isSelected?: boolean;
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });

  return (
    <MenuItem
      onClick={onClick}
      backgroundColor={
        isSelected ? colorStyle.background.selected : 'transparent'
      }
      _hover={{
        backgroundColor: isSelected
          ? colorStyle.background.highlighted
          : colorStyle.background.tertiary,
      }}
    >
      {label}
      {count !== undefined && <Text ml="auto">{count}</Text>}
    </MenuItem>
  );
};

const ReportedEmailDetails = ({
  timeRange,
}: {
  timeRange: EuqTimeRangeEnum;
}) => {
  const { orgId } = useOrgStore();
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });

  // Service type filter is only shown once Gmail add-in config is enabled
  const hideEuqServiceTypeFilter =
    !customConfig.ui.featureToggle.gmailAddInConfigEnabled;

  // Track previous orgId to only reset filters when it actually changes
  const prevOrgIdRef = useRef<string>();

  // Column management state with persistence
  const COLUMN_CONFIG_VERSION = 4;

  const getStoredColumnConfig = useCallback(() => {
    const storageKey = `euq-cecp-columns-${orgId}`;
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Check if version matches, if not, reset to default
        if (parsed.version !== COLUMN_CONFIG_VERSION) {
          localStorage.removeItem(storageKey);
        } else {
          // Always update column names with current translations
          return {
            ...parsed,
            order: hideEuqServiceTypeFilter
              ? (parsed.order as string[]).filter((id) => id !== 'service_type')
              : parsed.order,
            columnNames: {
              created_at: t('operations.euq.detail.table.header.reportedAt'),
              message_arrive_time: t(
                'operations.euq.detail.table.header.arrivingTime'
              ),
              message_subject: t('operations.euq.detail.table.header.subject'),
              message_sender: t('operations.euq.detail.table.header.sender'),
              message_recipients: t(
                'operations.euq.detail.table.header.recipient'
              ),
              requested_by: t('operations.euq.detail.table.header.reporter'),
              service_type: t('operations.euq.filter.serviceType'),
              category: t('operations.euq.filter.reportedType'),
              request_source: t(
                'administration.addIn.autoCollect.collectSource.column'
              ),
              approval_status: t(
                'operations.euq.detail.table.header.approvalStatus'
              ),
              gtoc_result_category: t('operations.euq.table.analysis'),
              mitigation_status: t('operations.euq.table.securityActions'),
            },
          };
        }
      }
    } catch (error) {
      console.warn('Failed to load column config from localStorage:', error);
    }

    // Return default config
    return {
      order: [
        'created_at',
        'message_arrive_time',
        'message_subject',
        'message_sender',
        'message_recipients',
        'requested_by',
        ...(hideEuqServiceTypeFilter ? [] : ['service_type']),
        'category',
        'request_source',
        'approval_status',
        'gtoc_result_category',
        'mitigation_status',
      ],
      hideIds: [] as string[],
      columnNames: {
        created_at: t('operations.euq.detail.table.header.reportedAt'),
        message_arrive_time: t(
          'operations.euq.detail.table.header.arrivingTime'
        ),
        message_subject: t('operations.euq.detail.table.header.subject'),
        message_sender: t('operations.euq.detail.table.header.sender'),
        message_recipients: t('operations.euq.detail.table.header.recipient'),
        requested_by: t('operations.euq.detail.table.header.reporter'),
        service_type: t('operations.euq.filter.serviceType'),
        category: t('operations.euq.filter.reportedType'),
        request_source: t(
          'administration.addIn.autoCollect.collectSource.column'
        ),
        approval_status: t('operations.euq.detail.table.header.approvalStatus'),
        gtoc_result_category: t('operations.euq.table.analysis'),
        mitigation_status: t('operations.euq.table.securityActions'),
      } as { [columnId: string]: string },
    };
  }, [orgId, t, hideEuqServiceTypeFilter]);

  const [columnConfig, setColumnConfig] = useState(() =>
    getStoredColumnConfig()
  );

  // Save column config to localStorage when it changes
  const saveColumnConfig = useCallback(
    (newConfig: typeof columnConfig) => {
      const storageKey = `euq-cecp-columns-${orgId}`;
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({
            version: COLUMN_CONFIG_VERSION,
            order: newConfig.order,
            hideIds: newConfig.hideIds,
          })
        );
        setColumnConfig(newConfig);
      } catch (error) {
        console.warn('Failed to save column config to localStorage:', error);
        setColumnConfig(newConfig);
      }
    },
    [orgId]
  );

  // Modal states
  const [isColumnsModalOpen, setIsColumnsModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isMitigationAlertOpen, setIsMitigationAlertOpen] = useState(true);

  // Start with 'All' to show all items by default
  const initialStatus = FeedbackFilterStatus.All;

  const {
    status,
    category,
    resultCategory,
    serviceType,
    keyword,
    sortKey,
    securityActions,
    requestSource,
    setStatus,
    setCategory,
    setResultCategory,
    setServiceType,
    setSortKey,
    setSecurityActions,
    setRequestSource,
    updateKeywordSubject,
    resetFilters,
    clearMainFilters,
  } = useFilters({
    initialStatus,
    timeRange,
  });

  const [debouncedKeyword, setDebouncedKeyword] =
    useState<FilterKeyword>(keyword);

  // Debounce the keyword updates for the query
  const debouncedSetKeyword = useMemo(
    () =>
      debounce((newKeyword: FilterKeyword) => {
        setDebouncedKeyword(newKeyword);
      }, FILTER_DEBOUNCE_DELAY),
    []
  );

  useEffect(() => {
    debouncedSetKeyword(keyword);
  }, [keyword, debouncedSetKeyword]);

  useEffect(() => {
    return () => {
      debouncedSetKeyword.cancel();
    };
  }, [debouncedSetKeyword]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });

  const resetPagination = useCallback(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  }, []);

  // Convert security actions to mitigation status
  // 'inProgress' -> 2, 'completed' -> 1, 'required' -> 4, 'all' -> null
  const mitigationStatus = useMemo(() => {
    if (securityActions === 'all') return null;
    if (securityActions === 'completed') return 1;
    if (securityActions === 'inProgress') return 2;
    if (securityActions === 'required') return 4;
    return null;
  }, [securityActions]);

  // Data query - fetch with filters
  const { data, isFetching, isPending, error } = useEuqFeedbackQuery({
    status,
    category,
    resultCategory,
    serviceType,
    requestSource,
    timeRange,
    keyword: debouncedKeyword, // Use debounced keyword for the query
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
    sortKey,
    orgId,
    mitigationStatus,
  });

  const isLoading = useMemo(
    () => isFetching || isPending,
    [isFetching, isPending]
  );

  // Fetch filter counts
  const filterCounts = useFilterCounts({
    orgId,
    timeRange,
  });

  // Fetch mitigation status counts for alert
  const mitigationCounts = useMitigationStatusCounts({
    orgId,
    timeRange,
  });

  const { contentList, total } = useMemo(
    () => ({
      contentList: data?.contentList || [],
      total: data?.total || 0,
    }),
    [data]
  );

  const [rowSelection, setRowSelection] = useState<{
    [key: string]: boolean;
  }>({});
  const selectedIds = useMemo(
    () => Object.keys(rowSelection).filter((key) => rowSelection[key]),
    [rowSelection]
  );
  const selectedItemsData = useMemo(
    () =>
      contentList.filter((item) => selectedIds.includes(item.id.toString())),
    [contentList, selectedIds]
  );

  const [actionModalState, setActionModalState] = useState<ActionModalState>({
    name: null,
    id: null,
  });
  const resetActionModalState = useCallback(() => {
    setActionModalState({
      name: null,
      id: null,
    });
  }, []);

  const { approveMutation, rejectMutation } = useEuqFeedbackMutations({
    orgId,
    onSuccess: () => {
      setRowSelection({});
      resetPagination();
    },
  });

  // Reset filters only when orgId actually changes (not on initial load)
  useEffect(() => {
    if (prevOrgIdRef.current && prevOrgIdRef.current !== orgId) {
      resetFilters();
      resetPagination();
      setRowSelection({});
      // Reload column config for new organization
      setColumnConfig(getStoredColumnConfig());
    }
    prevOrgIdRef.current = orgId;
  }, [orgId, resetFilters, resetPagination, getStoredColumnConfig]);

  // Reset pagination when timeRange changes (but keep filters)
  useEffect(() => {
    resetPagination();
    setRowSelection({});
  }, [timeRange, resetPagination]);

  // Helper function to reset selections and pagination
  const resetSelectionsAndPagination = useCallback(() => {
    setRowSelection({});
    resetPagination();
  }, [resetPagination]);

  // Shared handler for filter changes
  const handleFilterChange = useCallback(
    (
      filterType:
        | 'status'
        | 'category'
        | 'resultCategory'
        | 'serviceType'
        | 'requestSource'
        | 'all',
      value?:
        | FeedbackFilterStatus
        | FeedbackFilterCategory
        | FeedbackResultCategory
        | ServiceTypeFilter
        | RequestSourceFilter
    ) => {
      resetSelectionsAndPagination();

      if (filterType === 'all') {
        // Clear all filters - this will remove them from URL in a single update
        clearMainFilters();
      } else if (filterType === 'status' && value) {
        setStatus(value as FeedbackFilterStatus);
      } else if (filterType === 'category' && value) {
        setCategory(value as FeedbackFilterCategory);
      } else if (filterType === 'resultCategory' && value) {
        setResultCategory(value as FeedbackResultCategory);
      } else if (filterType === 'serviceType' && value) {
        setServiceType(value as ServiceTypeFilter);
      } else if (filterType === 'requestSource' && value) {
        setRequestSource(value as RequestSourceFilter);
      }
    },
    [
      resetSelectionsAndPagination,
      setStatus,
      setCategory,
      setResultCategory,
      setServiceType,
      setRequestSource,
      clearMainFilters,
    ]
  );

  // Helper function to check if all filters are at default state (All)
  const isAllFiltersDefault =
    status === FeedbackFilterStatus.All &&
    category === FeedbackFilterCategory.All &&
    resultCategory === FeedbackResultCategory.All &&
    serviceType === ServiceTypeFilter.All &&
    requestSource === 'all';

  // Handle error
  if (error) {
    return <Text color="red">{t('common.apiException.internalError')}</Text>;
  }

  return (
    <>
      <Stack flex={1} gap="4x" mt="4x">
        <Text fontWeight="500" fontSize="20px" lineHeight="28px" pl="16px">
          {t('operations.euq.detail.title')}
        </Text>
        {mitigationCounts &&
          mitigationCounts.required > 0 &&
          isMitigationAlertOpen && (
            <Alert
              variant="outline"
              severity="info"
              mx="16px"
              onClose={() => setIsMitigationAlertOpen(false)}
            >
              <Flex justifyContent="space-between" alignItems="center">
                <Text>
                  <Trans
                    i18nKey="operations.euq.alert.manualMitigationRequired"
                    values={{ count: mitigationCounts.required }}
                    components={{
                      1: (
                        <LinkButton
                          onClick={() => {
                            setSecurityActions('required');
                            resetSelectionsAndPagination();
                          }}
                        />
                      ),
                    }}
                  />
                </Text>
                <AlertCloseButton width="max-content" height="max-content" />
              </Flex>
            </Alert>
          )}
        <Flex flex={1}>
          <Stack
            width={214}
            borderRight={1}
            borderColor={colorStyle.divider}
            pb="4x"
          >
            <FilterMenuItem
              onClick={() => handleFilterChange('all')}
              label={t('operations.euq.filter.allReportedEmails')}
              count={filterCounts?.all}
              isSelected={isAllFiltersDefault}
            />
            <MenuDivider />
            <MenuGroup
              title={t('operations.euq.detail.table.header.approvalStatus')}
            >
              <FilterMenuItem
                onClick={() =>
                  handleFilterChange('status', FeedbackFilterStatus.Created)
                }
                label={t('operations.euq.statusTab.created')}
                count={filterCounts?.status[FeedbackFilterStatus.Created]}
                isSelected={status === FeedbackFilterStatus.Created}
              />
              <FilterMenuItem
                onClick={() =>
                  handleFilterChange('status', FeedbackFilterStatus.Approved)
                }
                label={t('operations.euq.statusTab.approved')}
                count={filterCounts?.status[FeedbackFilterStatus.Approved]}
                isSelected={status === FeedbackFilterStatus.Approved}
              />
              <FilterMenuItem
                onClick={() =>
                  handleFilterChange('status', FeedbackFilterStatus.Rejected)
                }
                label={t('operations.euq.statusTab.rejected')}
                count={filterCounts?.status[FeedbackFilterStatus.Rejected]}
                isSelected={status === FeedbackFilterStatus.Rejected}
              />
              <FilterMenuItem
                onClick={() =>
                  handleFilterChange('status', FeedbackFilterStatus.Bypassed)
                }
                label={t('operations.euq.statusTab.bypassed')}
                count={filterCounts?.status[FeedbackFilterStatus.Bypassed]}
                isSelected={status === FeedbackFilterStatus.Bypassed}
              />
            </MenuGroup>
            <MenuDivider />
            <MenuGroup title={t('operations.euq.filter.reportedType')}>
              <FilterMenuItem
                onClick={() =>
                  handleFilterChange('category', FeedbackFilterCategory.Spam)
                }
                label={t('operations.euq.reportAs.type.spam')}
                count={filterCounts?.category[FeedbackFilterCategory.Spam]}
                isSelected={category === FeedbackFilterCategory.Spam}
              />
              <FilterMenuItem
                onClick={() =>
                  handleFilterChange(
                    'category',
                    FeedbackFilterCategory.Phishing
                  )
                }
                label={t('operations.euq.reportAs.type.phishing')}
                count={filterCounts?.category[FeedbackFilterCategory.Phishing]}
                isSelected={category === FeedbackFilterCategory.Phishing}
              />
              <FilterMenuItem
                onClick={() =>
                  handleFilterChange('category', FeedbackFilterCategory.NotSpam)
                }
                label={t('operations.euq.reportAs.type.noRisk')}
                count={filterCounts?.category[FeedbackFilterCategory.NotSpam]}
                isSelected={category === FeedbackFilterCategory.NotSpam}
              />
            </MenuGroup>

            {!hideEuqServiceTypeFilter && (
              <>
                <MenuDivider />
                <MenuGroup title={t('operations.euq.filter.serviceType')}>
                  <FilterMenuItem
                    onClick={() =>
                      handleFilterChange(
                        'serviceType',
                        ServiceTypeFilter.Outlook
                      )
                    }
                    label={t('operations.euq.filter.serviceType.outlook')}
                    count={
                      filterCounts?.serviceType?.[ServiceTypeFilter.Outlook]
                    }
                    isSelected={serviceType === ServiceTypeFilter.Outlook}
                  />
                  <FilterMenuItem
                    onClick={() =>
                      handleFilterChange('serviceType', ServiceTypeFilter.Gmail)
                    }
                    label={t('operations.euq.filter.serviceType.gmail')}
                    count={filterCounts?.serviceType?.[ServiceTypeFilter.Gmail]}
                    isSelected={serviceType === ServiceTypeFilter.Gmail}
                  />
                </MenuGroup>
              </>
            )}
            <MenuDivider />
            <MenuGroup
              title={t('administration.addIn.autoCollect.collectSource.column')}
            >
              <FilterMenuItem
                onClick={() =>
                  handleFilterChange('requestSource', 'AutoCollect')
                }
                label={t(
                  'administration.addIn.autoCollect.collectSource.autoCollect'
                )}
                count={filterCounts?.requestSource?.AutoCollect}
                isSelected={requestSource === 'AutoCollect'}
              />
              <FilterMenuItem
                onClick={() => handleFilterChange('requestSource', 'Plugin')}
                label={t(
                  'administration.addIn.autoCollect.collectSource.plugin'
                )}
                count={filterCounts?.requestSource?.Plugin}
                isSelected={requestSource === 'Plugin'}
              />
              <FilterMenuItem
                onClick={() => handleFilterChange('requestSource', 'Banner')}
                label={t(
                  'administration.addIn.autoCollect.collectSource.banner'
                )}
                count={filterCounts?.requestSource?.Banner}
                isSelected={requestSource === 'Banner'}
              />
              <FilterMenuItem
                onClick={() =>
                  handleFilterChange('requestSource', 'CentralOperation')
                }
                label={t(
                  'administration.addIn.autoCollect.collectSource.adminReport'
                )}
                count={filterCounts?.requestSource?.AdminReport}
                isSelected={requestSource === 'CentralOperation'}
              />
            </MenuGroup>
            <MenuDivider />
            <MenuGroup title={t('operations.euq.table.analysis')}>
              <FilterMenuItem
                onClick={() =>
                  handleFilterChange(
                    'resultCategory',
                    FeedbackResultCategory.InProgress
                  )
                }
                label={t('operations.euq.analysis.inProgress')}
                count={
                  filterCounts?.resultCategory[
                    FeedbackResultCategory.InProgress
                  ]
                }
                isSelected={
                  resultCategory === FeedbackResultCategory.InProgress
                }
              />
              <FilterMenuItem
                onClick={() =>
                  handleFilterChange(
                    'resultCategory',
                    FeedbackResultCategory.Spam
                  )
                }
                label={t('operations.euq.analysis.completedSpam')}
                count={
                  filterCounts?.resultCategory[FeedbackResultCategory.Spam]
                }
                isSelected={resultCategory === FeedbackResultCategory.Spam}
              />
              <FilterMenuItem
                onClick={() =>
                  handleFilterChange(
                    'resultCategory',
                    FeedbackResultCategory.Phishing
                  )
                }
                label={t('operations.euq.analysis.completedPhishing')}
                count={
                  filterCounts?.resultCategory[FeedbackResultCategory.Phishing]
                }
                isSelected={resultCategory === FeedbackResultCategory.Phishing}
              />
              <FilterMenuItem
                onClick={() =>
                  handleFilterChange(
                    'resultCategory',
                    FeedbackResultCategory.Normal
                  )
                }
                label={t('operations.euq.analysis.completedNotARisk')}
                count={
                  filterCounts?.resultCategory[FeedbackResultCategory.Normal]
                }
                isSelected={resultCategory === FeedbackResultCategory.Normal}
              />
              <FilterMenuItem
                onClick={() =>
                  handleFilterChange(
                    'resultCategory',
                    FeedbackResultCategory.Flagged
                  )
                }
                label={t('operations.euq.analysis.completedFlagged')}
                count={
                  filterCounts?.resultCategory[FeedbackResultCategory.Flagged]
                }
                isSelected={resultCategory === FeedbackResultCategory.Flagged}
              />
              <FilterMenuItem
                onClick={() =>
                  handleFilterChange(
                    'resultCategory',
                    FeedbackResultCategory.Error
                  )
                }
                label={t('operations.euq.analysis.failed')}
                count={
                  filterCounts?.resultCategory[FeedbackResultCategory.Error]
                }
                isSelected={resultCategory === FeedbackResultCategory.Error}
              />
            </MenuGroup>
          </Stack>
          <Stack gap="2x" flex={1}>
            <Flex px="3x" gap="3x">
              <SearchInput
                width="200px"
                placeholder={t('operations.euq.detail.table.header.subject')}
                value={keyword.subject}
                onChange={(e: any) => {
                  const value = e.target.value;
                  setRowSelection({});
                  resetPagination();
                  updateKeywordSubject(value);
                }}
                onClearInput={() => {
                  setRowSelection({});
                  resetPagination();
                  updateKeywordSubject('');
                }}
              />

              <AddFilterMenu
                status={status}
                setStatus={setStatus}
                category={category}
                setCategory={setCategory}
                resultCategory={resultCategory}
                setResultCategory={setResultCategory}
                serviceType={serviceType}
                setServiceType={setServiceType}
                securityActions={securityActions}
                setSecurityActions={setSecurityActions}
                requestSource={requestSource}
                setRequestSource={setRequestSource}
                hideServiceType={hideEuqServiceTypeFilter}
                resetSelectionsAndPagination={resetSelectionsAndPagination}
              />

              <Flex mr="4x" alignItems="start" gap="3x" flex={1}>
                {/* Only show action buttons when there are selected items */}
                {selectedIds.length > 0 && (
                  <>
                    <DownloadBtn selectedIds={selectedIds} />
                    {/* Show approve/reject buttons for items that can be acted upon */}
                    <ApproveBtn
                      selectedIds={selectedIds}
                      selectedItemsData={selectedItemsData}
                      refreshData={() => {
                        setRowSelection({});
                        resetPagination();
                      }}
                    />
                    <RejectBtn
                      selectedIds={selectedIds}
                      selectedItemsData={selectedItemsData}
                      refreshData={() => {
                        setRowSelection({});
                        resetPagination();
                      }}
                    />
                  </>
                )}
                {/* Always show manage columns and export buttons */}
                <Flex ml="auto">
                  <IconButton
                    tooltipLabel={t('log.button.manageColumns')}
                    width={32}
                    height={32}
                    onClick={() => setIsColumnsModalOpen(true)}
                  >
                    <Icon as={ColumnsIcon} />
                  </IconButton>
                  <IconButton
                    tooltipLabel={t('log.button.export')}
                    width={32}
                    height={32}
                    onClick={() => setIsExportModalOpen(true)}
                  >
                    <Icon as={ExportIcon} />
                  </IconButton>
                </Flex>
              </Flex>
            </Flex>
            <Flex minHeight={600} flex={1}>
              <DetailsTable
                data={contentList}
                sortKey={sortKey}
                setSortKey={(v: string) => {
                  resetSelectionsAndPagination();
                  setSortKey(v);
                }}
                setActionModalState={setActionModalState}
                rowSelection={rowSelection}
                onRowSelectionChange={setRowSelection}
                isLoading={isLoading}
                pagination={pagination}
                onPaginationChange={setPagination}
                rowCount={total}
                pageSizeOptions={[10, 20, 50, 100]}
                columnConfig={columnConfig}
              />
            </Flex>
          </Stack>
        </Flex>
      </Stack>
      <RejectAndApproveModal
        actionModalState={actionModalState}
        resetActionModalState={resetActionModalState}
        approveMutation={approveMutation}
        rejectMutation={rejectMutation}
      />
      {isColumnsModalOpen && (
        <ManageColumnsModal
          visible={isColumnsModalOpen}
          columnConfig={columnConfig}
          onSave={saveColumnConfig}
          onClose={() => setIsColumnsModalOpen(false)}
        />
      )}
      {isExportModalOpen && (
        <ExportModal
          visible={isExportModalOpen}
          columns={columnConfig.order
            .filter((id: any) => !columnConfig.hideIds.includes(id))
            .map((id: any) => ({
              id,
              header: columnConfig.columnNames[id],
            }))}
          data={contentList}
          filterParams={{
            status,
            category,
            resultCategory,
            requestSource,
            serviceType,
            timeRange,
            keyword: debouncedKeyword,
            sortKey,
            mitigationStatus,
          }}
          onClose={() => setIsExportModalOpen(false)}
        />
      )}
    </>
  );
};

export default ReportedEmailDetails;

const RejectAndApproveModal = ({
  actionModalState,
  resetActionModalState,
  approveMutation,
  rejectMutation,
}: {
  actionModalState: ActionModalState;
  resetActionModalState: () => void;
  approveMutation: any;
  rejectMutation: any;
}) => {
  const { t } = useTranslation();

  const onReject = async () => {
    try {
      await rejectMutation.mutateAsync([actionModalState.id as string]);
    } finally {
      resetActionModalState();
    }
  };

  const onApprove = async () => {
    try {
      await approveMutation.mutateAsync([actionModalState.id as string]);
    } finally {
      resetActionModalState();
    }
  };

  return (
    <>
      <ActionModal
        isOpen={actionModalState.name === 'approve'}
        onClose={resetActionModalState}
        title={t('operations.euq.action.approve.title')}
        body={t('operations.euq.action.approve.desc')}
        confirmText={t('common.action.label.approve')}
        onConfirm={onApprove}
      />
      <ActionModal
        isOpen={actionModalState.name === 'reject'}
        onClose={resetActionModalState}
        title={t('operations.euq.action.reject.title')}
        body={t('operations.euq.action.reject.desc')}
        confirmText={t('common.action.label.reject')}
        onConfirm={onReject}
      />
    </>
  );
};
